import { memo, useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { DraftNotice } from '../../components/DraftNotice';
import { isPlanError } from '../../components/plan/planErrors';
import { PLAN_LABEL, upgradePath } from '../../components/plan/planLabels';
import { UpsellFromError } from '../../components/plan/Upsell';
import { usePlanName } from '../../components/plan/usePlan';
import { TicketHead } from '../../components/TicketHead';
import { OptionChips } from '../../components/OptionChips';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type {
  AssetRead,
  IconRead,
  LogoPosition,
  PictureTreatment,
  SlideBackground,
  SlideItemEditUpdate,
  SlideItemRead,
  SlideLayerEditUpdate,
  SlideLayoutTemplate,
  SlideOverlayStyle,
  SlidePhotoBox,
  SlidePoint,
  SlideRead,
} from '../../types/api';
import { assetFileName } from '../../lib/storage';
import { updateSlideLayers } from '../generation/generationApi';
import {
  PICTURE_TREATMENT_LABELS,
  PICTURE_TREATMENT_ORDER,
  iconLabel,
  isPictureTreatment,
  readPictureTreatment,
} from '../generation/pictureStyles';
import { useIcons } from '../generation/useIcons';
import { assetsApi } from '../knowledge-base/knowledgeBaseApi';
import { useBrandIdentity } from '../knowledge-base/useBrandIdentity';
import { useCollection } from '../knowledge-base/useCollection';
import { useSignedAssetUrl } from '../knowledge-base/useSignedAssetUrl';
import { CommunityDesignPreview } from '../templates/CommunityDesignPreview';
import { brandPalette } from '../templates/communityZones';
import { SlideBackgroundPicker } from './SlideBackgroundPicker';
import { NudgePad } from './NudgePad';
import {
  SlideCanvas,
  type CanvasLayer,
  type CommunityPreview,
  type SlideCanvasControl,
} from './SlideCanvas';
import { slideRoleTag } from './slideRole';
import { formatFraction, presetLogoPoint, samePoint } from './slideGeometry';
import { iconGlyph } from './slideSources';
import { isPictureBackground, treatmentApplies } from './slideTreatments';
import {
  restoreSlideDraft,
  slideDraftForm,
  useSlideDraft,
  type SlideDraftForm,
  type SlideDraftItem,
} from './useSlideDraft';
import {
  SLIDE_TEMPLATE_GROUPS,
  SLIDE_TEMPLATES,
  hasImageSlot,
  headlineCapFor,
} from './slideTemplates';

/** Field bounds, mirroring the backend's `SlideLayerEditUpdate` validators. */
const HEADLINE_MAX_LENGTH = 90;
const BODY_MAX_LENGTH = 180;
const EYEBROW_MAX_LENGTH = 40;
const ITEM_TITLE_MAX_LENGTH = 60;
const ITEM_SUBTITLE_MAX_LENGTH = 80;
const ITEM_VALUE_MAX_LENGTH = 20;
/** The widest item count any item layout draws (menu rows). */
const ITEMS_MAX_COUNT = 8;

/** Said by the editor and by the approve-flush alike when a field is unusable. */
const NOT_SAVEABLE_MESSAGE =
  'These slide edits are not ready to save. Fix the fields above, or cancel the edit.';

/** One item row under edit, keyed so reorders keep their inputs. */
interface EditableItem extends SlideDraftItem {
  key: string;
}

let nextItemKey = 0;

function toEditableItems(items: readonly SlideDraftItem[]): EditableItem[] {
  return items.map((item) => {
    nextItemKey += 1;
    return { key: `slide-item-${nextItemKey}`, ...item };
  });
}

/** The same rows without their local keys — what a draft stores. */
function toDraftItems(entries: readonly EditableItem[]): SlideDraftItem[] {
  return entries.map((entry) => ({
    title: entry.title,
    subtitle: entry.subtitle,
    value: entry.value,
    asset_path: entry.asset_path,
  }));
}

function itemsPayload(entries: readonly EditableItem[]): SlideItemEditUpdate[] {
  return entries.map((entry) => ({
    title: entry.title.trim(),
    subtitle: entry.subtitle.trim() || null,
    value: entry.value.trim() || null,
    asset_path: entry.asset_path,
  }));
}

function sameItems(next: SlideItemEditUpdate[], current: SlideItemRead[]): boolean {
  if (next.length !== current.length) return false;
  return next.every((entry, index) => {
    const stored = current[index];
    return (
      stored !== undefined &&
      entry.title === stored.title &&
      (entry.subtitle ?? null) === stored.subtitle &&
      (entry.value ?? null) === stored.value &&
      (entry.asset_path ?? null) === stored.asset_path
    );
  });
}

/** The four band treatments, mirroring the renderer's overlay styles. */
const OVERLAY_STYLES: readonly { value: SlideOverlayStyle; label: string; blurb: string }[] = [
  { value: 'faded', label: 'Faded', blurb: 'The classic soft panel behind the caption.' },
  {
    value: 'frosted',
    label: 'Frosted',
    blurb: 'Blurs the photo behind the words, tints it lightly.',
  },
  {
    value: 'clear',
    label: 'Clear',
    blurb: 'No panel — falls back to a soft one if the photo is too busy to read over.',
  },
  {
    value: 'duotone',
    label: 'Duotone',
    blurb: 'Picture tinted in the brand colours, no band.',
  },
];

/** Share of a limit at which the counter starts warning. */
const NEAR_LIMIT_RATIO = 0.9;

/** The nudge pad's name for each canvas layer. */
const LAYER_NAMES: Readonly<Record<CanvasLayer, string>> = {
  caption: 'Caption',
  logo: 'Logo',
  photo: 'Photo',
};

/** The five preset placements. Free positioning is dragging's job, not a sixth preset. */
const LOGO_POSITIONS: readonly { value: LogoPosition; label: string }[] = [
  { value: 'top_left', label: 'Top left' },
  { value: 'top_right', label: 'Top right' },
  { value: 'bottom_left', label: 'Bottom left' },
  { value: 'bottom_right', label: 'Bottom right' },
  { value: 'none', label: 'No logo' },
];

function counterClassName(count: number, limit: number): string {
  if (count > limit) return 'layer-editor__count layer-editor__count--over';
  if (count >= limit * NEAR_LIMIT_RATIO) return 'layer-editor__count layer-editor__count--near';
  return 'layer-editor__count';
}

/** Longest text any template field may hold, whatever its box allows. */
const TEMPLATE_FIELD_MAX_LENGTH = 300;

/** One text box of a v2 community design, as the review panel edits it. */
interface TemplateFieldSpec {
  id: string;
  label: string;
  maxChars: number;
  placeholder: string;
  multiline: boolean;
}

/** An id said as a label, for a slide whose template map could not be read. */
function labelFromId(id: string): string {
  const words = id.replace(/_/g, ' ').trim();
  return words.length > 0 ? words.charAt(0).toUpperCase() + words.slice(1) : id;
}

/** The typed text, trimmed, with blank boxes left out — what the server stores. */
function templateFieldsPayload(
  specs: readonly TemplateFieldSpec[],
  values: Record<string, string>,
): Record<string, string> {
  const next: Record<string, string> = {};
  for (const spec of specs) {
    const value = (values[spec.id] ?? '').trim();
    if (value.length > 0) next[spec.id] = value;
  }
  return next;
}

function sameFields(left: Record<string, string>, right: Record<string, string>): boolean {
  const leftKeys = Object.keys(left);
  if (leftKeys.length !== Object.keys(right).length) return false;
  return leftKeys.every((key) => left[key] === right[key]);
}

/** Both null, or the same box (rounded like points are). */
function samePhotoBox(left: SlidePhotoBox | null, right: SlidePhotoBox | null): boolean {
  if (left === null || right === null) return left === right;
  const round = (value: number) => Math.round(value * 10000) / 10000;
  return (
    round(left.x) === round(right.x) &&
    round(left.y) === round(right.y) &&
    round(left.w) === round(right.w) &&
    round(left.h) === round(right.h)
  );
}

/** Structural compare — the union's arms have no shared shape to diff loosely. */
function sameBackground(left: SlideBackground, right: SlideBackground): boolean {
  if (left.kind !== right.kind) return false;
  if (left.kind === 'color' && right.kind === 'color') return left.color === right.color;
  if (left.kind === 'gradient' && right.kind === 'gradient') {
    return left.from === right.from && left.to === right.to;
  }
  if (left.kind === 'asset' && right.kind === 'asset') {
    return left.asset_path === right.asset_path;
  }
  if (left.kind === 'generated' && right.kind === 'generated') {
    return left.asset_path === right.asset_path;
  }
  if (left.kind === 'pattern' && right.kind === 'pattern') {
    return left.pattern === right.pattern && left.ground === right.ground && left.ink === right.ink;
  }
  return false;
}

/** Shown in place of the edit controls on a slide whose words the image model drew. */
export const AI_TEXT_NOTE = 'Text on this slide was drawn by AI. Use Redesign to change it.';

/** Message for the domain errors a layer edit can provoke. */
function editMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'REQUEST_NOT_EDITABLE':
        return 'This post is no longer waiting for your approval, so its slides are locked. Reload to see where it stands.';
      case 'SLIDE_NOT_FOUND':
        return 'That slide is no longer here. Reload the post.';
      case 'ASSET_NOT_FOUND':
        return 'That image is no longer in your brand profile. Pick another background.';
      case 'SLIDE_TEXT_IN_IMAGE':
      case 'slide_text_in_image':
        return AI_TEXT_NOTE;
      default:
        return caught.message;
    }
  }
  return getErrorMessage(caught, 'That edit did not save. Try again.');
}

/* ------------------------------------------------------------------ */
/* Panel sections                                                      */
/*                                                                     */
/* Each one is memoised on props that a pointer gesture cannot change.  */
/* Dropping a layer commits a fraction into this component's state, so  */
/* the stage has to re-render — the pickers below it do not, and this   */
/* is what keeps that commit (and every keystroke in the text fields)   */
/* from rebuilding the whole form.                                     */
/* ------------------------------------------------------------------ */

interface SlideItemsFieldProps {
  items: readonly EditableItem[];
  /** The business's uploads, for the per-item photo select. */
  assets: readonly AssetRead[];
  isLoadingAssets: boolean;
  disabled: boolean;
  onPatch: (key: string, patch: Partial<SlideDraftItem>) => void;
  onMove: (index: number, delta: -1 | 1) => void;
  onRemove: (key: string) => void;
  onAdd: () => void;
}

const SlideItemsField = memo(function SlideItemsField({
  items,
  assets,
  isLoadingAssets,
  disabled,
  onPatch,
  onMove,
  onRemove,
  onAdd,
}: SlideItemsFieldProps) {
  return (
    <fieldset className="pack-items pack-items--editor" disabled={disabled}>
      <legend className="field__label">Items on this slide</legend>
      <span className="field__hint">
        The rows this layout draws — a member, a dish, a benefit. Photos swap among your images and
        the canvas above previews them; the saved image is still the truth.
      </span>
      <ol className="pack-items__list">
        {items.map((entry, index) => (
          <li className="pack-item" key={entry.key}>
            <div className="pack-item__head">
              <span className="pack-item__index">{index + 1}</span>
              <div className="pack-item__order">
                <button
                  className="button button--ghost pack-item__move"
                  type="button"
                  aria-label={`Move item ${index + 1} up`}
                  disabled={disabled || index === 0}
                  onClick={() => onMove(index, -1)}
                >
                  ↑
                </button>
                <button
                  className="button button--ghost pack-item__move"
                  type="button"
                  aria-label={`Move item ${index + 1} down`}
                  disabled={disabled || index === items.length - 1}
                  onClick={() => onMove(index, 1)}
                >
                  ↓
                </button>
                <button
                  className="button button--ghost button--danger-text"
                  type="button"
                  disabled={disabled || items.length <= 1}
                  onClick={() => onRemove(entry.key)}
                >
                  Remove
                </button>
              </div>
            </div>
            <div className="pack-item__fields">
              <label className="field">
                <span className="field__label">Title</span>
                <input
                  className="field__input"
                  type="text"
                  maxLength={ITEM_TITLE_MAX_LENGTH}
                  value={entry.title}
                  disabled={disabled}
                  onChange={(event) => onPatch(entry.key, { title: event.target.value })}
                />
              </label>
              <label className="field">
                <span className="field__label">Subtitle</span>
                <input
                  className="field__input"
                  type="text"
                  maxLength={ITEM_SUBTITLE_MAX_LENGTH}
                  value={entry.subtitle}
                  disabled={disabled}
                  onChange={(event) => onPatch(entry.key, { subtitle: event.target.value })}
                />
              </label>
              <label className="field pack-item__value">
                <span className="field__label">Detail</span>
                <input
                  className="field__input"
                  type="text"
                  maxLength={ITEM_VALUE_MAX_LENGTH}
                  value={entry.value}
                  disabled={disabled}
                  onChange={(event) => onPatch(entry.key, { value: event.target.value })}
                />
              </label>
            </div>
            <select
              className="field__input pack-item__asset-select"
              aria-label={`Photo for item ${index + 1}`}
              value={entry.asset_path ?? ''}
              disabled={disabled || isLoadingAssets}
              onChange={(event) => onPatch(entry.key, { asset_path: event.target.value || null })}
            >
              <option value="">No photo — initials placeholder</option>
              {assets.map((asset) => (
                <option key={asset.id} value={asset.storage_path}>
                  {assetFileName(asset.storage_path)}
                </option>
              ))}
            </select>
          </li>
        ))}
      </ol>
      <button
        className="button"
        type="button"
        disabled={disabled || items.length >= ITEMS_MAX_COUNT}
        onClick={onAdd}
      >
        Add an item
      </button>
    </fieldset>
  );
});

interface LayoutFieldProps {
  slideId: string;
  value: SlideLayoutTemplate;
  /** The layout wants a picture and the background is a flat fill. */
  hasEmptySlot: boolean;
  /** No `text_position` in play: the template still owns the block. */
  isTemplatePlaced: boolean;
  disabled: boolean;
  onChange: (next: SlideLayoutTemplate) => void;
}

const LayoutField = memo(function LayoutField({
  slideId,
  value,
  hasEmptySlot,
  isTemplatePlaced,
  disabled,
  onChange,
}: LayoutFieldProps) {
  return (
    <fieldset className="logo-field" disabled={disabled}>
      <legend className="field__label">Layout</legend>
      <div className="layout-templates" role="radiogroup" aria-label="Caption layout">
        {SLIDE_TEMPLATE_GROUPS.map((group) => (
          <div className="layout-template-group" key={group.kind}>
            <span className="layout-template-group__title">{group.label}</span>
            <div className="layout-template-group__options">
              {group.templates.map((template) => {
                const option = SLIDE_TEMPLATES[template];
                const isSelected = template === value;
                return (
                  <label
                    className={
                      isSelected ? 'layout-option layout-option--selected' : 'layout-option'
                    }
                    key={template}
                  >
                    <input
                      className="visually-hidden"
                      type="radio"
                      name={`layout-template-${slideId}`}
                      value={template}
                      checked={isSelected}
                      onChange={() => onChange(template)}
                    />
                    <span className="layout-option__label">{option.label}</span>
                    <span className="layout-option__blurb">{option.blurb}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {hasEmptySlot ? (
        <span className="field__hint">
          This layout shows a picture beside the caption — pick an image or generated background to
          fill it.
        </span>
      ) : null}
      <span className="field__hint">
        {isTemplatePlaced
          ? 'The layout places the caption, so editing the words reflows it.'
          : 'The caption is pinned where you put it, so the layout only shapes the panel behind it.'}
      </span>
    </fieldset>
  );
});

interface CaptionPanelFieldProps {
  slideId: string;
  overlayStyle: SlideOverlayStyle;
  cardEnabled: boolean;
  disabled: boolean;
  onOverlayChange: (next: SlideOverlayStyle) => void;
  onCardChange: (next: boolean) => void;
}

const CaptionPanelField = memo(function CaptionPanelField({
  slideId,
  overlayStyle,
  cardEnabled,
  disabled,
  onOverlayChange,
  onCardChange,
}: CaptionPanelFieldProps) {
  return (
    <fieldset className="logo-field" disabled={disabled}>
      <legend className="field__label">Caption panel</legend>
      <div className="layout-templates" role="radiogroup" aria-label="Overlay style">
        {OVERLAY_STYLES.map((option) => {
          const isSelected = option.value === overlayStyle;
          return (
            <label
              className={isSelected ? 'layout-option layout-option--selected' : 'layout-option'}
              key={option.value}
            >
              <input
                className="visually-hidden"
                type="radio"
                name={`overlay-style-${slideId}`}
                value={option.value}
                checked={isSelected}
                onChange={() => onOverlayChange(option.value)}
              />
              <span className="layout-option__label">{option.label}</span>
              <span className="layout-option__blurb">{option.blurb}</span>
            </label>
          );
        })}
      </div>
      <label className="layer-editor__card-toggle">
        <input
          type="checkbox"
          checked={cardEnabled}
          disabled={disabled}
          onChange={(event) => onCardChange(event.target.checked)}
        />
        <span>Put the caption on a card</span>
      </label>
      <span className="field__hint">
        A rounded panel behind the words. Its colour follows the text automatically.
      </span>
    </fieldset>
  );
});

/** What each treatment is for, as the design agent hears it (T6). Tooltips only. */
const TREATMENT_BLURBS: Readonly<Record<PictureTreatment, string>> = {
  full_bleed: 'The picture is the slide.',
  framed: 'An editorial, gallery feel: the picture in a bordered frame on a clean panel.',
  masked: 'A friendly, poster-like shape: a circle or an arch on the panel.',
  cutout: 'The subject as the hero on brand colour, lifted off its background.',
  grain: 'Analog warmth: film grain and softened blacks.',
  halftone: 'Print, zine, bold graphic: the picture as a dot screen.',
  tritone: 'Strongly on-brand: the picture mapped to three brand tones.',
};

interface TreatmentFieldProps {
  slideId: string;
  value: PictureTreatment;
  /** The theme's suggestions, read tolerantly: unknown entries are dropped. */
  suggested: readonly PictureTreatment[];
  /** False when the chosen layout composes its own picture and ignores treatments. */
  isDrawn: boolean;
  disabled: boolean;
  onChange: (next: PictureTreatment) => void;
}

const TreatmentField = memo(function TreatmentField({
  slideId,
  value,
  suggested,
  isDrawn,
  disabled,
  onChange,
}: TreatmentFieldProps) {
  const options = PICTURE_TREATMENT_ORDER.map((treatment) => ({
    value: treatment,
    label: PICTURE_TREATMENT_LABELS[treatment],
    detail: suggested.includes(treatment) ? 'suggested' : undefined,
    title: TREATMENT_BLURBS[treatment],
  }));
  return (
    <OptionChips
      legend="Treatment"
      hint={
        isDrawn
          ? 'How the picture is presented. A redraw of the same picture: no AI, nothing used.'
          : 'This layout draws its own picture, so the treatment applies once you pick another layout.'
      }
      options={options}
      selected={[value]}
      onToggle={(next) => {
        if (isPictureTreatment(next)) onChange(next);
      }}
      mode="single"
      name={`picture-treatment-${slideId}`}
      disabled={disabled}
    />
  );
});

/** The icons grouped by category, the server's curated order kept throughout. */
function groupIcons(icons: readonly IconRead[]): { category: string; icons: IconRead[] }[] {
  const groups: { category: string; icons: IconRead[] }[] = [];
  for (const icon of icons) {
    const group = groups.find((entry) => entry.category === icon.category);
    if (group) group.icons.push(icon);
    else groups.push({ category: icon.category, icons: [icon] });
  }
  return groups;
}

/** Does an icon answer the search? Its name, its words, or its category. */
function matchesIconQuery(icon: IconRead, query: string): boolean {
  if (query.length === 0) return true;
  return (
    icon.name.includes(query) ||
    iconLabel(icon.name).toLowerCase().includes(query) ||
    icon.category.toLowerCase().includes(query)
  );
}

interface IconFieldProps {
  slideId: string;
  /** The curated name on the slide, or null for none. */
  value: string | null;
  icons: readonly IconRead[];
  isLoading: boolean;
  error: string | null;
  disabled: boolean;
  onChange: (next: string | null) => void;
}

/**
 * "No icon", or one of the curated icons (IMAGE_VARIETY_PLAN §14 S6), each
 * drawn in the vendored Lucide font from its codepoint. Radios, so the arrow
 * keys walk the grid; the search narrows it without touching the choice.
 */
const IconField = memo(function IconField({
  slideId,
  value,
  icons,
  isLoading,
  error,
  disabled,
  onChange,
}: IconFieldProps) {
  const [query, setQuery] = useState('');
  const trimmedQuery = query.trim().toLowerCase();
  const groups = useMemo(
    () => groupIcons(icons.filter((icon) => matchesIconQuery(icon, trimmedQuery))),
    [icons, trimmedQuery],
  );
  const name = `slide-icon-${slideId}`;
  const isKnown = value === null || icons.some((icon) => icon.name === value);

  return (
    <fieldset className="icon-field" disabled={disabled}>
      <legend className="field__label">Icon</legend>
      <span className="field__hint">
        One glyph in the brand accent, placed by the layout. Reads best on a colour or pattern
        background. A re-render, no AI call.
      </span>

      <label className={value === null ? 'icon-none icon-none--selected' : 'icon-none'}>
        <input
          className="visually-hidden"
          type="radio"
          name={name}
          checked={value === null}
          onChange={() => onChange(null)}
        />
        No icon
      </label>

      {!isKnown && value !== null ? (
        <p className="field__hint">On this slide now: {iconLabel(value)}.</p>
      ) : null}

      {isLoading ? (
        <p className="kb-section__status" role="status">
          Loading the icons
        </p>
      ) : icons.length > 0 ? (
        <>
          <input
            className="field__input icon-field__search"
            type="search"
            placeholder="Search icons"
            aria-label="Search icons"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="icon-field__groups">
            {groups.length === 0 ? (
              <p className="field__hint">No icon matches that search.</p>
            ) : (
              groups.map((group) => (
                <div
                  className="icon-field__group"
                  key={group.category}
                  role="group"
                  aria-label={iconLabel(group.category)}
                >
                  <p className="icon-field__category">{iconLabel(group.category)}</p>
                  <div className="icon-grid">
                    {group.icons.map((entry) => {
                      const isSelected = entry.name === value;
                      const label = iconLabel(entry.name);
                      return (
                        <label
                          className={
                            isSelected ? 'icon-option icon-option--selected' : 'icon-option'
                          }
                          key={entry.name}
                          title={label}
                        >
                          <input
                            className="visually-hidden"
                            type="radio"
                            name={name}
                            value={entry.name}
                            checked={isSelected}
                            aria-label={label}
                            onChange={() => onChange(entry.name)}
                          />
                          <span className="icon-option__glyph" aria-hidden="true">
                            {iconGlyph(entry.codepoint)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      ) : null}
      {error ? <p className="field__hint">{error}</p> : null}
    </fieldset>
  );
});

interface LogoFieldProps {
  slideId: string;
  value: LogoPosition;
  /** The logo has been dragged, so no preset is the current answer. */
  isPlacedByHand: boolean;
  disabled: boolean;
  onChange: (next: LogoPosition) => void;
}

const LogoField = memo(function LogoField({
  slideId,
  value,
  isPlacedByHand,
  disabled,
  onChange,
}: LogoFieldProps) {
  return (
    <fieldset className="logo-field" disabled={disabled}>
      <legend className="field__label">Logo position</legend>
      <div className="logo-positions" role="radiogroup" aria-label="Logo position">
        {LOGO_POSITIONS.map((option) => {
          const isSelected = option.value === value && !isPlacedByHand;
          return (
            <label
              className={isSelected ? 'logo-option logo-option--selected' : 'logo-option'}
              key={option.value}
            >
              <input
                className="visually-hidden"
                type="radio"
                name={`logo-position-${slideId}`}
                value={option.value}
                checked={isSelected}
                onChange={() => onChange(option.value)}
              />
              {option.label}
            </label>
          );
        })}
      </div>
      <span className="field__hint">
        {isPlacedByHand
          ? 'Placed by hand. Pick a corner to snap it back to a preset.'
          : 'Four preset corners, or no logo on this slide. Drag the logo on the canvas to place it anywhere else.'}
      </span>
    </fieldset>
  );
});

/** One slide's unsaved edits, and the way to write them without leaving here. */
export interface PendingSlideEdit {
  slideId: string;
  /** Saves through the same PUT the Save button uses. Rejects on failure. */
  flush: () => Promise<void>;
}

interface LayerEditorProps {
  businessId: string;
  requestId: string;
  /** The slide being edited. Mount this component keyed on its id. */
  slide: SlideRead;
  /**
   * This run rendered one picture, not a deck — a generated single. Drops the
   * slide number from the ticket head, where it would be numbering a set of one.
   */
  isOnlySlide?: boolean;
  /** Every AI backdrop this run produced. Empty is normal — the tab hides. */
  backdrops: string[];
  /**
   * The community template behind this run's slides, when there is one and the
   * gallery still lists it. Null degrades the stage to the classic block.
   */
  community?: CommunityPreview | null;
  /** The saved slide, straight from the server. Its render is queued, not done. */
  onSaved: (slide: SlideRead) => void;
  onClose: () => void;
  /** The request moved past final review while the editor was open. */
  onStale: () => void;
  /**
   * Raised when this slide gains or loses unsaved edits. The parent uses it to
   * write them before it approves, so an approval never ships a picture that
   * does not match what is on screen. Must be stable across renders.
   */
  onUnsavedChange?: (pending: PendingSlideEdit | null) => void;
  /**
   * View only: the plan has no post editor. The stage shows the slide as it
   * is, with no drag, no inspector panel and no save; a stored draft is neither
   * restored nor touched. Approving and exporting are the parent's and stay open.
   */
  readOnly?: boolean;
}

/**
 * Constrained canvas editor for one slide.
 *
 * A stage on the left, where the caption block and the logo are dragged into
 * place, and a panel on the right for the things a pointer cannot express:
 * the words, the background, and which corner a logo returns to. The whole
 * point is still what it will not do (SRS §3) — brand colours, fonts, text
 * colour and layer order have no controls here, and saving re-renders the picture
 * from the same template rather than calling the model, which is what makes
 * editing the sane alternative to a fourth regeneration.
 *
 * Only changed fields are sent. The server answers with `rendered_path` back to
 * null while the new picture is drawn, so the editor stays open over its own
 * live preview instead of handing the user a placeholder to stare at.
 *
 * Saving stays an explicit act, because a save is a render: nudging a logo five
 * times has to cost one picture, not five. What that used to cost is a refresh
 * away from losing the lot, so the work is mirrored to `localStorage` as it is
 * typed (`useSlideDraft`) and offered back on the way in — and an approval
 * flushes whatever is still unsaved before it closes the request.
 */
export function LayerEditor({
  businessId,
  requestId,
  slide,
  isOnlySlide = false,
  backdrops,
  community = null,
  onSaved,
  onClose,
  onStale,
  onUnsavedChange,
  readOnly = false,
}: LayerEditorProps) {
  // Read once, before any state exists: recovered work is on screen from the
  // first render rather than swapped in by an effect. View only shows the slide
  // as saved: a draft it could never save would only mislead.
  const [restored] = useState(() =>
    readOnly ? { form: slideDraftForm(slide), savedAt: null } : restoreSlideDraft(slide),
  );
  const [headline, setHeadline] = useState(restored.form.headline);
  const [body, setBody] = useState(restored.form.body);
  const [eyebrow, setEyebrow] = useState(restored.form.eyebrow);
  const [overlayStyle, setOverlayStyle] = useState<SlideOverlayStyle>(restored.form.overlayStyle);
  const [cardEnabled, setCardEnabled] = useState(restored.form.cardEnabled);
  const [background, setBackground] = useState<SlideBackground>(restored.form.background);
  const [logoPosition, setLogoPosition] = useState<LogoPosition>(restored.form.logoPosition);
  // Null means "the template places it". An unpinned slide starts there, so the
  // preview shows what the next render will actually do rather than freezing the
  // block where this one happened to land.
  const [textPosition, setTextPosition] = useState<SlidePoint | null>(restored.form.textPosition);
  const [layoutTemplate, setLayoutTemplate] = useState<SlideLayoutTemplate>(
    restored.form.layoutTemplate,
  );
  const [pictureTreatment, setPictureTreatment] = useState<PictureTreatment>(
    restored.form.pictureTreatment,
  );
  const [icon, setIcon] = useState<string | null>(restored.form.icon);
  const [logoPoint, setLogoPoint] = useState<SlidePoint | null>(restored.form.logoPoint);
  // The anniversary poster's photo box. Null means the template's own box.
  const [photoBox, setPhotoBox] = useState<SlidePhotoBox | null>(restored.form.photoBox);
  // The structured items an item template draws (a member, menu rows, offer
  // benefits). Empty on every classic slide, and the panel hides with it.
  const [items, setItems] = useState<EditableItem[]>(() => toEditableItems(restored.form.items));
  // The canvas layer last focused or tapped: what the nudge pad moves, and under
  // a finger the one layer that takes a drag. View state only; never saved.
  const [selectedLayer, setSelectedLayer] = useState<CanvasLayer | null>(null);
  const canvasRef = useRef<SlideCanvasControl>(null);
  // A v2 community slide's text, one value per text box of its design.
  const [templateFields, setTemplateFields] = useState<Record<string, string>>(
    restored.form.templateFields,
  );
  // The preset the logo returns to when it is switched back on.
  const [logoPreset, setLogoPreset] = useState<LogoPosition>(() =>
    slide.logo_position !== 'none'
      ? slide.logo_position
      : restored.form.logoPosition !== 'none'
        ? restored.form.logoPosition
        : 'top_left',
  );
  const [error, setError] = useState<string | null>(null);
  // A 402 from the save (the plan lost the post editor mid-session).
  const [planError, setPlanError] = useState<ApiError | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const planName = usePlanName();

  const { identity } = useBrandIdentity(businessId);
  const iconList = useIcons();
  const iconCodepoint =
    icon === null ? null : (iconList.icons.find((entry) => entry.name === icon)?.codepoint ?? null);
  const { url: logoUrl } = useSignedAssetUrl(identity?.logo_path ?? null);
  const hasItemLayer = slide.items.length > 0;

  // A community slide drawn on a v2 design is edited box by box. The map comes
  // from the gallery entry, or the business's own template when it is not in
  // the gallery (`useCommunityPreview`); while it is unreadable — gone from both,
  // a list did not load — a slide that already carries template text is still
  // edited by its ids.
  const templateMap = community?.zones.v === 2 ? community.zones : null;
  const isTemplateV2 =
    templateMap !== null ||
    (layoutTemplate === 'community' && Object.keys(slide.template_fields).length > 0);
  const templateSpecs: TemplateFieldSpec[] = templateMap
    ? templateMap.texts.map((box) => ({
        id: box.id,
        label: box.label.trim() || labelFromId(box.id),
        maxChars: box.max_chars,
        placeholder: box.default,
        multiline: box.max_chars > 80 || box.max_lines > 2,
      }))
    : isTemplateV2
      ? Object.keys(slide.template_fields).map((id) => ({
          id,
          label: labelFromId(id),
          maxChars: TEMPLATE_FIELD_MAX_LENGTH,
          placeholder: '',
          multiline: true,
        }))
      : [];
  // The design says whether it has a logo at all; without the map, assume it does.
  const designHasLogo = templateMap === null || templateMap.logo.enabled;
  // The assets an item photo can swap to. Loaded only for item slides.
  const assets = useCollection(assetsApi, hasItemLayer ? businessId : null);

  // The chosen layout's own headline cap — 90 unless its shape is tighter. It
  // is advisory: the server takes up to 90 whatever the template and answers an
  // over-cap headline with a visual finding, so this styles the counter and
  // adds a line of explanation, and never disables Save.
  const headlineCap = headlineCapFor(layoutTemplate);
  const isTightCap = headlineCap < HEADLINE_MAX_LENGTH;
  // A picture slot with no picture in it: the layout renders its brand panel
  // and the guardrails file `image_slot_empty` against it. An icon fills the
  // slot (the renderer keeps the slot layout for it), so that is not empty.
  const hasEmptySlot =
    hasImageSlot(layoutTemplate) && !isPictureBackground(background) && icon === null;

  const trimmedHeadline = headline.trim();
  const trimmedBody = body.trim();
  const nextBody = trimmedBody.length > 0 ? trimmedBody : null;
  const trimmedEyebrow = eyebrow.trim();
  const nextEyebrow = trimmedEyebrow.length > 0 ? trimmedEyebrow : null;
  // What the canvas previews: the frozen tokens with the two editable knobs
  // swapped in. Accent and card geometry stay whatever the server resolved.
  const previewTheme = { ...slide.theme, overlay_style: overlayStyle, card_enabled: cardEnabled };
  // The frozen theme's suggestions; a theme frozen before treatments has none.
  const themeTreatments = slide.theme.treatments;
  const suggestedTreatments = useMemo(
    () => (themeTreatments ?? []).filter(isPictureTreatment),
    [themeTreatments],
  );
  const isCutoutPreview =
    pictureTreatment === 'cutout' && treatmentApplies(background, layoutTemplate);

  // The user handed placement back to the template on a slide that was pinned.
  // The server reads that from a `layout_template` arriving *without* a
  // `text_position`, so the template has to be sent even when it did not change.
  const isUnpinning = slide.text_position_locked && textPosition === null;

  const changes: SlideLayerEditUpdate = {};
  if (trimmedHeadline !== slide.headline) changes.headline = trimmedHeadline;
  if (nextBody !== slide.body) changes.body = nextBody;
  // Explicit null clears the kicker, exactly like body.
  if (nextEyebrow !== slide.eyebrow) changes.eyebrow = nextEyebrow;
  if (overlayStyle !== slide.theme.overlay_style) changes.overlay_style = overlayStyle;
  if (cardEnabled !== slide.theme.card_enabled) changes.card_enabled = cardEnabled;
  if (!sameBackground(background, slide.background)) changes.background = background;
  if (logoPosition !== slide.logo_position) changes.logo_position = logoPosition;
  if (textPosition !== null && !samePoint(textPosition, slide.text_position)) {
    changes.text_position = textPosition;
  }
  if (layoutTemplate !== slide.layout_template || isUnpinning) {
    changes.layout_template = layoutTemplate;
  }
  // Compared with the tolerant read, so an older slide without the field is
  // not "changed" to full bleed by merely opening the editor.
  if (pictureTreatment !== readPictureTreatment(slide.picture_treatment)) {
    changes.picture_treatment = pictureTreatment;
  }
  // Explicit null removes the icon, so the key is sent whenever it changed.
  if (icon !== (slide.icon ?? null)) changes.icon = icon;
  // Explicit null is the payload that clears a hand-placed logo, so this key is
  // sent whenever the point changed — including when it changed to nothing.
  if (!samePoint(logoPoint, slide.logo_point)) changes.logo_point = logoPoint;
  // Explicit null hands the photo back to the template's box, like logo_point.
  if (!samePhotoBox(photoBox, slide.photo_box)) changes.photo_box = photoBox;
  const nextItems = itemsPayload(items);
  if (hasItemLayer && !sameItems(nextItems, slide.items)) changes.items = nextItems;
  // Whole-dict replace: the boxes of this design, blank ones left out.
  const nextTemplateFields = templateFieldsPayload(templateSpecs, templateFields);
  if (isTemplateV2 && !sameFields(nextTemplateFields, slide.template_fields)) {
    changes.template_fields = nextTemplateFields;
  }
  const templateFieldsAreValid = templateSpecs.every(
    (spec) => (templateFields[spec.id] ?? '').trim().length <= spec.maxChars,
  );

  const itemsAreValid =
    !hasItemLayer || (items.length > 0 && items.every((entry) => entry.title.trim().length > 0));
  const hasChanges = Object.keys(changes).length > 0;
  // A v2 design's words live in its boxes, so the headline column is the
  // server's to derive and nothing here asks for one.
  const isValid =
    (isTemplateV2 || trimmedHeadline.length > 0) &&
    trimmedHeadline.length <= HEADLINE_MAX_LENGTH &&
    templateFieldsAreValid &&
    trimmedBody.length <= BODY_MAX_LENGTH &&
    trimmedEyebrow.length <= EYEBROW_MAX_LENGTH &&
    itemsAreValid;

  // The live form, as the draft stores it. Nothing derived, nothing trimmed:
  // it is the editor's own state, so restoring it puts the cursor back where
  // the user left it rather than where a validator would.
  const form: SlideDraftForm = {
    headline,
    body,
    eyebrow,
    overlayStyle,
    cardEnabled,
    background,
    logoPosition,
    textPosition,
    layoutTemplate,
    pictureTreatment,
    icon,
    logoPoint,
    photoBox,
    items: toDraftItems(items),
    templateFields,
  };
  const draft = useSlideDraft(slide, form, restored.savedAt, !readOnly);

  const patchItem = useCallback((key: string, patchValues: Partial<SlideDraftItem>) => {
    setItems((current) =>
      current.map((entry) => (entry.key === key ? { ...entry, ...patchValues } : entry)),
    );
  }, []);

  const moveItem = useCallback((index: number, delta: -1 | 1) => {
    setItems((current) => {
      const target = index + delta;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      const [row] = next.splice(index, 1);
      if (row === undefined) return current;
      next.splice(target, 0, row);
      return next;
    });
  }, []);

  const removeItem = useCallback((key: string) => {
    setItems((current) => current.filter((row) => row.key !== key));
  }, []);

  const addItem = useCallback(() => {
    setItems((current) => [
      ...current,
      ...toEditableItems([{ title: '', subtitle: '', value: '', asset_path: null }]),
    ]);
  }, []);

  const isTextPositionMoved =
    textPosition === null
      ? slide.text_position_locked
      : !samePoint(textPosition, slide.text_position);
  const shownLogoPoint =
    logoPoint ?? presetLogoPoint(logoPosition, SLIDE_TEMPLATES[layoutTemplate].textBox);
  // A selection whose layer has left the stage (logo set to none, a layout
  // with no photo) selects nothing.
  const nudgeLayer =
    (selectedLayer === 'logo' && logoPosition === 'none') ||
    (selectedLayer === 'photo' && (layoutTemplate !== 'anniversary' || items.length === 0))
      ? null
      : selectedLayer;

  /** A preset owns the logo again; the hand-placed point goes with it. */
  const chooseLogoPosition = useCallback((next: LogoPosition) => {
    setLogoPosition(next);
    setLogoPoint(null);
  }, []);

  /**
   * Picking a layout hands the caption to it — the pin comes off. This is the
   * server's own rule (`layout_template` without a `text_position` unpins), so
   * without it a pinned slide previewed the caption at its old spot while the
   * saved render moved it to wherever the new layout puts captions: the two
   * disagreed on exactly the edit the picker exists for. Dragging afterwards
   * pins it again, and Reset puts the old point back.
   */
  const chooseLayout = useCallback((next: SlideLayoutTemplate) => {
    setLayoutTemplate(next);
    setTextPosition(null);
  }, []);

  /** Puts every field back to a known set of values — the slide's, on discard. */
  const applyForm = useCallback((next: SlideDraftForm) => {
    setHeadline(next.headline);
    setBody(next.body);
    setEyebrow(next.eyebrow);
    setOverlayStyle(next.overlayStyle);
    setCardEnabled(next.cardEnabled);
    setBackground(next.background);
    setLogoPosition(next.logoPosition);
    setTextPosition(next.textPosition);
    setLayoutTemplate(next.layoutTemplate);
    setPictureTreatment(next.pictureTreatment);
    setIcon(next.icon);
    setLogoPoint(next.logoPoint);
    setPhotoBox(next.photoBox);
    setItems(toEditableItems(next.items));
    setTemplateFields(next.templateFields);
  }, []);

  /**
   * The logo on a v2 design is on or off: off is `logo_position: 'none'`, on
   * is the preset it had before (the design's own box renders it either way).
   */
  function setLogoShown(shown: boolean) {
    if (shown) {
      setLogoPosition(logoPreset);
    } else {
      if (logoPosition !== 'none') setLogoPreset(logoPosition);
      setLogoPosition('none');
    }
  }

  function discardDraft() {
    draft.discard();
    applyForm(slideDraftForm(slide));
  }

  /** The save under way, so a second caller joins it instead of racing it. */
  const inFlightRef = useRef<Promise<void> | null>(null);

  async function putChanges(): Promise<void> {
    setError(null);
    setPlanError(null);
    setIsSaving(true);
    try {
      const saved = await updateSlideLayers(businessId, requestId, slide.id, changes);
      // The work is on the server now; the local copy of it has done its job.
      draft.clear();
      // The parent swaps the slide in and starts polling for the new render.
      // This stays mounted so the layout on screen is the one just saved rather
      // than an empty "rendering" box.
      onSaved(saved);
      setHasSaved(true);
      setIsSaving(false);
    } catch (caught) {
      if (isPlanError(caught)) setPlanError(caught);
      else setError(editMessage(caught));
      setIsSaving(false);
      if (caught instanceof ApiError && caught.code === 'REQUEST_NOT_EDITABLE') {
        onStale();
      }
      throw caught;
    }
  }

  /**
   * The one write path. The Save button and the parent's approve-flush both go
   * through it, so there is a single place where a layer edit becomes a render.
   * It rejects rather than swallowing, because the caller that did not start it
   * has to know not to carry on.
   */
  async function saveChanges(): Promise<void> {
    // A save already in the air is *the* save: joining it keeps an approval
    // from overtaking a PUT that is still landing, and keeps one edit to one
    // render even when both buttons are pressed.
    const running = inFlightRef.current;
    if (running !== null) {
      await running;
      return;
    }

    if (!hasChanges) return;
    if (!isValid) {
      setError(NOT_SAVEABLE_MESSAGE);
      throw new Error(NOT_SAVEABLE_MESSAGE);
    }

    const attempt = putChanges();
    inFlightRef.current = attempt;
    try {
      await attempt;
    } finally {
      inFlightRef.current = null;
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSaving || readOnly) return;
    // Already reported in `error`; the rejection exists for the approve-flush.
    void saveChanges().catch(() => undefined);
  }

  // The parent holds a flush that never goes stale: the identity is fixed for
  // the life of the editor, and it always runs the current render's save.
  const saveRef = useRef(saveChanges);
  useEffect(() => {
    saveRef.current = saveChanges;
  });
  const flush = useCallback(() => saveRef.current(), []);

  const reportRef = useRef(onUnsavedChange);
  useEffect(() => {
    reportRef.current = onUnsavedChange;
  }, [onUnsavedChange]);

  useEffect(() => {
    const report = reportRef.current;
    report?.(hasChanges ? { slideId: slide.id, flush } : null);
    // Closing the editor takes its unsaved work off the parent's books; the
    // draft in storage is what makes reopening it not a loss.
    return () => report?.(null);
  }, [flush, hasChanges, slide.id]);

  const roleTag = isOnlySlide ? null : slideRoleTag(slide.role);
  const hasBrief = roleTag !== null || slide.visual_concept !== null || slide.emphasis.length > 0;

  return (
    <form
      className={readOnly ? 'layer-editor layer-editor--view-only' : 'layer-editor'}
      onSubmit={handleSubmit}
      noValidate
    >
      <TicketHead
        label={isOnlySlide ? 'Edit layers' : `Edit slide ${slide.slide_index + 1}`}
        timestamp={slide.updated_at}
      />

      {hasBrief ? (
        // What the design agent meant this slide to do. Read-only for now.
        <div className="layer-editor__brief">
          {roleTag === null ? null : <span className="badge badge--neutral">{roleTag}</span>}
          {slide.visual_concept === null ? null : (
            <p className="layer-editor__concept" title={slide.visual_concept}>
              <span className="layer-editor__brief-label">Picture idea</span> {slide.visual_concept}
            </p>
          )}
          {slide.emphasis.length === 0 ? null : (
            <p className="layer-editor__emphasis">
              <span className="layer-editor__brief-label">Emphasis</span>
              {slide.emphasis.map((word, position) => (
                <span className="badge badge--neutral" key={`${position}-${word}`}>
                  {word}
                </span>
              ))}
            </p>
          )}
        </div>
      ) : null}

      {readOnly ? (
        <p className="view-only-strip" role="note">
          <span>View only on {planName ? PLAN_LABEL[planName] : 'your plan'}</span>
          <span aria-hidden="true">·</span>
          <Link to={upgradePath('solo')}>Upgrade to edit</Link>
        </p>
      ) : null}

      <p className="layer-editor__lede" hidden={readOnly}>
        {isTemplateV2
          ? 'Edit the words in each box of the design and switch the logo on or off. The design itself stays as its author made it. Saving redraws this slide — no AI, and it does not use a post.'
          : 'Drag the caption and the logo where you want them, and edit the words and the background beside them. Brand colours, fonts and layer order stay as we set them. Saving redraws this slide — no AI, and it does not use a post.'}
      </p>

      {draft.restoredAt === null ? null : (
        <DraftNotice
          savedAt={draft.restoredAt}
          message="Restored unsaved changes from your last session, edited"
          discardLabel="Discard changes"
          onDismiss={draft.dismiss}
          onDiscard={discardDraft}
        />
      )}

      <div className="layer-editor__grid">
        <div className="layer-editor__stage">
          {templateMap !== null ? (
            <CommunityDesignPreview
              className="layer-editor__design"
              zones={templateMap}
              imageUrl={community?.imageUrl ?? null}
              palette={brandPalette(identity?.colors)}
              fields={templateFields}
              fallbackHeadline={slide.headline}
              logoUrl={logoUrl}
              showLogo={logoPosition !== 'none'}
              itemValue={items[0]?.value ?? null}
              label="This slide's design with your text"
            />
          ) : (
            <SlideCanvas
              headline={headline}
              body={nextBody}
              eyebrow={nextEyebrow}
              theme={previewTheme}
              background={background}
              textColor={slide.text_color}
              textPosition={textPosition}
              layoutTemplate={layoutTemplate}
              scrimApplied={slide.scrim_applied}
              logoPosition={logoPosition}
              logoPoint={logoPoint}
              logoPath={identity?.logo_path ?? null}
              items={items}
              community={community}
              photoBox={photoBox}
              pictureTreatment={pictureTreatment}
              iconCodepoint={iconCodepoint}
              disabled={isSaving || readOnly}
              onTextPositionChange={setTextPosition}
              onLogoPointChange={setLogoPoint}
              onPhotoBoxChange={setPhotoBox}
              selectedLayer={nudgeLayer}
              onSelectLayer={setSelectedLayer}
              controlRef={canvasRef}
            />
          )}

          <p className="layer-editor__approximation">
            Preview approximate — final image renders on save.
            {isTemplateV2
              ? ' Every box stays where the design puts it; only its words and the logo change here.'
              : hasImageSlot(layoutTemplate) ||
                  SLIDE_TEMPLATES[layoutTemplate].kind === 'item' ||
                  layoutTemplate === 'community'
                ? ' The picture box, portraits and panels belong to the layout — pick a different layout to move them. The caption and the logo are the two layers that drag.'
                : ''}
            {isCutoutPreview
              ? ' The preview approximates the cut-out: the render lifts the subject off its background.'
              : ''}
          </p>

          {isTemplateV2 ? null : (
            <p className="layer-editor__readout">
              {textPosition === null
                ? `Caption placed by the ${SLIDE_TEMPLATES[layoutTemplate].label.toLowerCase()} layout`
                : `Caption ${formatFraction(textPosition.x)} / ${formatFraction(textPosition.y)}${
                    slide.text_position_locked ? ' (pinned)' : ''
                  }`}
              {shownLogoPoint === null
                ? ' · No logo'
                : ` · Logo ${formatFraction(shownLogoPoint.x)} / ${formatFraction(shownLogoPoint.y)}${
                    logoPoint === null ? ' (preset)' : ''
                  }`}
            </p>
          )}
        </div>

        {readOnly ? null : (
          <div className="layer-editor__panel">
            {isTemplateV2 ? null : (
              // 900px and down only (CSS): under a fingertip, placing by the percent.
              <NudgePad
                target={nudgeLayer === null ? null : LAYER_NAMES[nudgeLayer]}
                disabled={isSaving}
                onNudge={(dx, dy) => canvasRef.current?.nudge(dx, dy)}
              />
            )}
            {isTemplateV2 ? (
              <>
                <fieldset className="template-fields" disabled={isSaving}>
                  <legend className="field__label">Text on the design</legend>
                  <span className="field__hint">
                    One field per box. Leave one empty and the design's own sample text is used.
                  </span>
                  {templateSpecs.map((spec) => {
                    const value = templateFields[spec.id] ?? '';
                    const update = (next: string) =>
                      setTemplateFields((current) => ({ ...current, [spec.id]: next }));
                    return (
                      <div className="field-stack" key={spec.id}>
                        <label className="field">
                          <span className="field__label">{spec.label}</span>
                          {spec.multiline ? (
                            <textarea
                              className="field__input"
                              rows={3}
                              maxLength={spec.maxChars}
                              placeholder={spec.placeholder}
                              value={value}
                              onChange={(event) => update(event.target.value)}
                            />
                          ) : (
                            <input
                              className="field__input"
                              type="text"
                              maxLength={spec.maxChars}
                              placeholder={spec.placeholder}
                              value={value}
                              onChange={(event) => update(event.target.value)}
                            />
                          )}
                        </label>
                        <p className="layer-editor__meter">
                          <span className={counterClassName(value.length, spec.maxChars)}>
                            {value.length}/{spec.maxChars}
                          </span>
                        </p>
                      </div>
                    );
                  })}
                  {templateMap === null ? (
                    <span className="field__hint">
                      The design's field labels could not be loaded, so each box is named by its id.
                    </span>
                  ) : null}
                </fieldset>

                {designHasLogo ? (
                  <label className="zone-toggle">
                    <input
                      type="checkbox"
                      checked={logoPosition !== 'none'}
                      disabled={isSaving}
                      onChange={(event) => setLogoShown(event.target.checked)}
                    />
                    <span>
                      Show my logo
                      <span className="field__hint">
                        It sits where the design puts it. Off leaves this slide without a logo.
                      </span>
                    </span>
                  </label>
                ) : (
                  <p className="field__hint">This design has no logo.</p>
                )}

                {hasItemLayer ? (
                  <SlideItemsField
                    items={items}
                    assets={assets.items}
                    isLoadingAssets={assets.isLoading}
                    disabled={isSaving}
                    onPatch={patchItem}
                    onMove={moveItem}
                    onRemove={removeItem}
                    onAdd={addItem}
                  />
                ) : null}
              </>
            ) : (
              <>
                <div className="field-stack">
                  <label className="field">
                    <span className="field__label">Eyebrow</span>
                    <input
                      className="field__input"
                      type="text"
                      maxLength={EYEBROW_MAX_LENGTH}
                      value={eyebrow}
                      disabled={isSaving}
                      onChange={(event) => setEyebrow(event.target.value)}
                    />
                    <span className="field__hint">
                      Optional kicker above the headline, in the brand accent.
                    </span>
                  </label>
                  <p className="layer-editor__meter">
                    <span className={counterClassName(eyebrow.length, EYEBROW_MAX_LENGTH)}>
                      {eyebrow.length}/{EYEBROW_MAX_LENGTH}
                    </span>
                  </p>
                </div>

                <div className="field-stack">
                  <label className="field">
                    <span className="field__label">Headline</span>
                    <input
                      className="field__input"
                      type="text"
                      maxLength={HEADLINE_MAX_LENGTH}
                      value={headline}
                      disabled={isSaving}
                      onChange={(event) => setHeadline(event.target.value)}
                    />
                  </label>
                  <p className="layer-editor__meter">
                    <span className={counterClassName(headline.length, headlineCap)}>
                      {headline.length}/{headlineCap}
                    </span>
                    <span className="layer-editor__meter-label">
                      {isTightCap
                        ? `characters — ${SLIDE_TEMPLATES[layoutTemplate].label.toLowerCase()} has room for ${headlineCap}`
                        : 'characters on this slide'}
                    </span>
                  </p>
                  {isTightCap && headline.length > headlineCap ? (
                    <span className="field__hint">
                      Longer than this layout is shaped for. It will still save — the renderer steps
                      the type down and the visual notes will say so.
                    </span>
                  ) : null}
                </div>

                <div className="field-stack">
                  <label className="field">
                    <span className="field__label">Body</span>
                    <textarea
                      className="field__input"
                      rows={3}
                      maxLength={BODY_MAX_LENGTH}
                      value={body}
                      disabled={isSaving}
                      onChange={(event) => setBody(event.target.value)}
                    />
                    <span className="field__hint">
                      Optional — leave it empty for a headline-only slide.
                    </span>
                  </label>
                  <p className="layer-editor__meter">
                    <span className={counterClassName(body.length, BODY_MAX_LENGTH)}>
                      {body.length}/{BODY_MAX_LENGTH}
                    </span>
                  </p>
                </div>

                {hasItemLayer ? (
                  <SlideItemsField
                    items={items}
                    assets={assets.items}
                    isLoadingAssets={assets.isLoading}
                    disabled={isSaving}
                    onPatch={patchItem}
                    onMove={moveItem}
                    onRemove={removeItem}
                    onAdd={addItem}
                  />
                ) : null}

                <SlideBackgroundPicker
                  businessId={businessId}
                  backdrops={backdrops}
                  value={background}
                  onChange={setBackground}
                  disabled={isSaving}
                />

                <LayoutField
                  slideId={slide.id}
                  value={layoutTemplate}
                  hasEmptySlot={hasEmptySlot}
                  isTemplatePlaced={textPosition === null}
                  disabled={isSaving}
                  onChange={chooseLayout}
                />

                {isPictureBackground(background) ? (
                  <TreatmentField
                    slideId={slide.id}
                    value={pictureTreatment}
                    suggested={suggestedTreatments}
                    isDrawn={treatmentApplies(background, layoutTemplate)}
                    disabled={isSaving}
                    onChange={setPictureTreatment}
                  />
                ) : null}

                <IconField
                  slideId={slide.id}
                  value={icon}
                  icons={iconList.icons}
                  isLoading={iconList.isLoading}
                  error={iconList.error}
                  disabled={isSaving}
                  onChange={setIcon}
                />

                <CaptionPanelField
                  slideId={slide.id}
                  overlayStyle={overlayStyle}
                  cardEnabled={cardEnabled}
                  disabled={isSaving}
                  onOverlayChange={setOverlayStyle}
                  onCardChange={setCardEnabled}
                />

                <LogoField
                  slideId={slide.id}
                  value={logoPosition}
                  isPlacedByHand={logoPoint !== null}
                  disabled={isSaving}
                  onChange={chooseLogoPosition}
                />

                <div className="layer-editor__position-actions">
                  <button
                    className="button button--ghost"
                    type="button"
                    disabled={isSaving || !isTextPositionMoved}
                    onClick={() => setTextPosition(slide.text_position)}
                  >
                    Reset caption position
                  </button>
                  {layoutTemplate === 'anniversary' ? (
                    <button
                      className="button button--ghost"
                      type="button"
                      disabled={isSaving || photoBox === null}
                      onClick={() => setPhotoBox(null)}
                    >
                      Reset photo
                    </button>
                  ) : null}
                  {slide.text_position_locked ? (
                    <button
                      className="button button--ghost"
                      type="button"
                      disabled={isSaving || textPosition === null}
                      onClick={() => setTextPosition(null)}
                    >
                      Use template layout
                    </button>
                  ) : null}
                  <span className="field__hint">
                    {slide.text_position_locked
                      ? 'Reset goes back to where the current render has it; the template takes the caption back off its pin.'
                      : 'Back to where the current render has it.'}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      <UpsellFromError error={planError} />

      {readOnly ? (
        <div className="layer-editor__actions">
          <button className="button" type="button" onClick={onClose}>
            Close
          </button>
        </div>
      ) : (
        <div className="layer-editor__actions">
          <button
            className="button button--primary"
            type="submit"
            disabled={!hasChanges || !isValid || isSaving}
          >
            {isSaving ? 'Saving' : 'Save and re-render'}
          </button>
          <button className="button" type="button" disabled={isSaving} onClick={onClose}>
            {hasSaved && !hasChanges ? 'Close' : 'Cancel'}
          </button>
          {hasChanges ? null : hasSaved ? (
            <p className="layer-editor__note layer-editor__note--saved" role="status">
              Saved. The new image is rendering — this preview is your layout until it lands.
            </p>
          ) : (
            <p className="layer-editor__note">Nothing changed yet.</p>
          )}
        </div>
      )}
    </form>
  );
}
